import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/orbit/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    workspace_name: "", password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Workspace created — welcome!");
      navigate("/orbit/app/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Registration failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="orbit-register-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A1F44] text-white items-center justify-center p-16">
        <div className="max-w-md">
          <div className="text-[11px] uppercase tracking-[0.22em] text-blue-300 font-semibold">Orbit CRM</div>
          <h1 className="mt-4 text-4xl font-semibold leading-tight">Start your workspace.<br/>Own your pipeline in minutes.</h1>
          <ul className="mt-8 space-y-3 text-sm text-white/80">
            <li>• Single source of truth for accounts, contacts, and deals</li>
            <li>• Built for outbound-first revenue teams</li>
            <li>• AI-ready timeline from day one</li>
          </ul>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-semibold text-[#0A1F44]">Create your workspace</h2>
          <p className="mt-2 text-sm text-slate-500">You&apos;ll be the workspace owner. Invite teammates after signup.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="orbit-register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">First name</label>
                <input type="text" required value={form.first_name} onChange={set("first_name")}
                  data-testid="orbit-register-first-name"
                  className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Last name</label>
                <input type="text" required value={form.last_name} onChange={set("last_name")}
                  data-testid="orbit-register-last-name"
                  className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Workspace name</label>
              <input type="text" required minLength={2} value={form.workspace_name} onChange={set("workspace_name")}
                data-testid="orbit-register-workspace"
                placeholder="e.g. Sozos Global"
                className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Work email</label>
              <input type="email" required value={form.email} onChange={set("email")}
                data-testid="orbit-register-email"
                className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Password</label>
              <input type="password" required minLength={8} value={form.password} onChange={set("password")}
                data-testid="orbit-register-password"
                placeholder="At least 8 characters"
                className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="orbit-register-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="orbit-register-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#0A1F44] text-white py-2.5 rounded-md text-sm font-semibold hover:bg-[#1E40AF] transition disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create workspace
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/orbit/login" className="text-[#1E40AF] font-medium hover:underline" data-testid="orbit-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
